// src/data/adapters/ticajeOfflineAdapter.js

/** Claves en localStorage para intervenciones */
export const KEYS = {
  active: "pwa.ticaje.activo",
  outbox: "pwa.ticaje.outbox",
};

const jget = (k, def = null) => {
  try {
    const s = localStorage.getItem(k);
    return s ? JSON.parse(s) : def;
  } catch {
    return def;
  }
};
const jset = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {}
};
const jdel = (k) => {
  try {
    localStorage.removeItem(k);
  } catch {}
};

const uuid = () =>
  crypto?.randomUUID?.() || `u_${Date.now()}_${Math.random().toString(36).slice(2)}`;

/** Devuelve la intervención activa local (o null) */
export function getLocalActive() {
  return jget(KEYS.active, null);
}
export function setLocalActive(a) {
  jset(KEYS.active, a);
}
export function clearLocalActive() {
  jdel(KEYS.active);
}

/** Outbox */
function getOutbox() {
  return jget(KEYS.outbox, []);
}
function setOutbox(list) {
  jset(KEYS.outbox, list);
}
function pushOutbox(item) {
  const l = getOutbox();
  l.push(item);
  setOutbox(l);
}

/**
 * Adaptador offline para intervenciones (ticajes).
 * Gestiona:
 *  - intervención local activa (stub o nacida offline)
 *  - cola outbox con eventos START / FINISH / CANCEL
 */
export const TicajeOfflineAdapter = {
  getActive() {
    const a = getLocalActive();
    if (!a) return null;
    return a; // ya está en forma "UI"
  },

  /**
   * Inicia una intervención local y encola START.
   * payload esperado:
   *  {
   *    cliente_id, centro_id, orden_id,
   *    comentarios,
   *    latitud, longitud, fingerprint,
   *    inicio, inicio_client,
   *    orden_titulo, cliente_nombre, centro_nombre
   *  }
   */
  start({
    cliente_id,
    centro_id,
    orden_id,
    comentarios,
    latitud,
    longitud,
    fingerprint,
    inicio,
    inicio_client,
    orden_titulo,
    cliente_nombre,
    centro_nombre,
  }) {
    if (getLocalActive()) {
      throw new Error("Ya existe una intervención local activa");
    }

    const ts = inicio || new Date().toISOString();
    const ic = inicio_client || ts; // inicio_client que mandaremos al backend

    const groupKey = uuid(); // idempotency key compartida por todo el ciclo

    const localActive = {
      id: `local:${uuid()}`,
      inicio: ts,
      cliente_id: cliente_id ?? null,
      centro_id:  centro_id ?? null,
      orden_id:   orden_id ?? null,
      comentarios: comentarios || null,

      orden_titulo:   orden_titulo || "",
      cliente_nombre: cliente_nombre || "",
      centro_nombre:  centro_nombre || "",

      latitud_inicio:  latitud ?? null,
      longitud_inicio: longitud ?? null,

      _groupKey: groupKey,
      pendingFinish: false,
      pendingCancel: false,
    };

    setLocalActive(localActive);

    // En el outbox guardamos inicio + inicio_client para el replay offline
    pushOutbox({
      id: uuid(),          // se usará como posible idemKey en el OnlineAdapter
      groupKey,
      type: "START",
      payload: {
        cliente_id,
        centro_id,
        orden_id,
        comentarios: comentarios || null,
        latitud,
        longitud,
        fingerprint,
        inicio: ts,
        inicio_client: ic,
      },
      ts: Date.now(),
    });

    return { status: "queued", active: localActive };
  },

  /**
   * Marca FINISH en outbox.
   * Soporta dos casos:
   *  - Intervención nacida offline (hay localActive).
   *  - Intervención nacida online pero se cierra offline (no hay localActive).
   *
   * payload debe incluir:
   *  {
   *    motivo_fin,
   *    latitud_cierre, longitud_cierre,
   *    en_rango_cierre, distancia_m_cierre,
   *    fin_client,
   *    // opcional: contexto para stub si no hay localActive
   *    cliente_id, centro_id, orden_id,
   *    orden_titulo, cliente_nombre, centro_nombre,
   *    inicio, latitud_inicio, longitud_inicio
   *  }
   */
  finish(payload) {
    const existing = getLocalActive();
    const hasLocal = !!existing;
    const groupKey = hasLocal ? (existing._groupKey || uuid()) : uuid();

    // "Stub" local que verá la UI mientras está pendiente
    const base = hasLocal
      ? existing
      : {
          id: `local:${uuid()}`,
          inicio: payload.inicio || payload.fin_client || new Date().toISOString(),

          cliente_id: payload.cliente_id ?? null,
          centro_id:  payload.centro_id ?? null,
          orden_id:   payload.orden_id ?? null,

          orden_titulo:   payload.orden_titulo || "",
          cliente_nombre: payload.cliente_nombre || "",
          centro_nombre:  payload.centro_nombre || "",

          latitud_inicio:  payload.latitud_inicio ?? null,
          longitud_inicio: payload.longitud_inicio ?? null,
        };

    const updatedActive = {
      ...base,
      _groupKey: groupKey,
      pendingFinish: payload,
      pendingCancel: false,
    };

    setLocalActive(updatedActive);

    const {
      motivo_fin = "finalizada",
      latitud_cierre,
      longitud_cierre,
      en_rango_cierre,
      distancia_m_cierre,
      fin_client,
    } = payload;

    const fc = fin_client || new Date().toISOString();

    pushOutbox({
      id: uuid(),
      groupKey,
      type: "FINISH",
      payload: {
        motivo_fin,
        latitud_cierre,
        longitud_cierre,
        en_rango_cierre,
        distancia_m_cierre,
        fin_client: fc,
      },
      ts: Date.now(),
    });

    return { status: "queued", active: updatedActive };
  },

  /**
   * Marca CANCEL en outbox.
   * También soporta intervención nacida online (sin localActive previa).
   */
  cancel() {
    const existing = getLocalActive();
    const hasLocal = !!existing;
    const groupKey = hasLocal ? (existing._groupKey || uuid()) : uuid();

    const base = hasLocal
      ? existing
      : {
          id: `local:${uuid()}`,
          inicio: new Date().toISOString(),
          cliente_id: null,
          centro_id:  null,
          orden_id:   null,
          orden_titulo:   "",
          cliente_nombre: "",
          centro_nombre:  "",
          latitud_inicio:  null,
          longitud_inicio: null,
        };

    const updatedActive = {
      ...base,
      _groupKey: groupKey,
      pendingFinish: false,
      pendingCancel: true,
    };

    setLocalActive(updatedActive);

    pushOutbox({
      id: uuid(),
      groupKey,
      type: "CANCEL",
      payload: { motivo_fin: "cancelada" },
      ts: Date.now(),
    });

    return { status: "queued", active: updatedActive };
  },

  /** ---- utilidades de reconciliación (las usa la store) ---- */
  _outboxList() {
    return getOutbox();
  },
  _outboxRemoveIds(ids) {
    const set = new Set(ids);
    setOutbox(getOutbox().filter((x) => !set.has(x.id)));
  },
  _clearActive() {
    clearLocalActive();
  },
};
